import type { DtoolsReviewSystem } from "./dtoolsStagingTransform";
import { collectOptionPrimaryComponentBlocks } from "./dtoolsOptionComponentGroups";
import { inferDtoolsEquipmentRole } from "./dtoolsEquipmentRoles";

function lineHaystack(blocks: ReturnType<typeof collectOptionPrimaryComponentBlocks>): string {
  return blocks
    .map((block) =>
      [
        block.line.manufacturer,
        block.line.model,
        block.line.category,
        block.line.polishedDescription,
        block.line.dtoolsDescription,
      ]
        .map((v) => String(v ?? "").trim())
        .filter(Boolean)
        .join(" "),
    )
    .join(" ");
}

function whyOptionSubject(displayName: string): string {
  const trimmed = displayName.trim();
  if (/^option\s+\d+(\s*[—–-]\s*.*)?$/i.test(trimmed)) return "This option";
  return trimmed;
}

function isPrivateCinemaSystem(
  blocks: ReturnType<typeof collectOptionPrimaryComponentBlocks>,
  haystack: string,
): boolean {
  const roles = blocks.map((block) => inferDtoolsEquipmentRole(block.line));
  const hasProjection = roles.some((role) =>
    ["Projection", "Video Processing", "Display"].includes(role),
  );
  const hasAmplification = roles.some((role) => role === "Amplification");
  const cinemaKeywords =
    /\b(cinema|theatre|theater|projector|madvr|kaleidescape|arcam|dream\s*vision|jbl\s*synthesis)\b/i.test(
      haystack,
    );
  return cinemaKeywords || (hasProjection && hasAmplification);
}

/** Short customer-facing rationale for an option — explains value, not category lists. */
export function buildWhyThisOptionText(system: DtoolsReviewSystem, displayName: string): string {
  const blocks = collectOptionPrimaryComponentBlocks(system);
  const haystack = lineHaystack(blocks);
  const subject = whyOptionSubject(displayName);

  if (/\b(lutron|homeworks|radio ra|ketra|lighting control|lighting system)\b/i.test(haystack)) {
    return `${subject} is focused on reliable day-to-day lighting control — scenes, keypads, and schedules that make the home feel simpler to live in, not a disconnected list of dimmers and processors.`;
  }

  if (isPrivateCinemaSystem(blocks, haystack)) {
    return `${subject} is focused on getting the cinema fundamentals right: a strong image chain, proper surround processing, dedicated amplification, and a reliable high-quality movie source. It gives the client a complete private cinema foundation that can deliver a much more convincing experience than a basic TV, soundbar, or streaming-based setup.`;
  }

  const productLabels: string[] = [];
  for (const block of blocks) {
    const label = [block.line.manufacturer, block.line.model].filter(Boolean).join(" ").trim();
    if (!label) continue;
    if (!productLabels.some((existing) => existing.toLowerCase() === label.toLowerCase())) {
      productLabels.push(label);
    }
    if (productLabels.length >= 2) break;
  }

  if (productLabels.length >= 2) {
    return `${subject} pairs ${productLabels[0]} with ${productLabels[1]} so the quoted scope works as one installed system — giving the client a clearer result than assembling parts separately.`;
  }

  if (productLabels.length === 1) {
    return `${subject} centres on ${productLabels[0]} with supporting equipment chosen to complete this scope as one coordinated installation.`;
  }

  return `${subject} delivers the equipment quoted for this home-technology scope as one coordinated installation.`;
}
